// Web Push in the browser (docs/plans/push-alerts.md): the subscription comes from the
// service worker's PushManager; the notifications themselves are shown by public/push-sw.js.
import type { AppPush, PushDevice } from '@ims/app'

function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4)
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function device(sub: PushSubscription | null): PushDevice | null {
  const json = sub?.toJSON()
  if (!json?.endpoint || !json.keys?.p256dh || !json.keys.auth) return null
  return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth }
}

const apple = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
const installed = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true

/** The service worker, or an error after a while (none runs in development). */
async function registration(): Promise<ServiceWorkerRegistration> {
  const found = await navigator.serviceWorker.getRegistration()
  if (found) return found
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Reload the app once, then try again.')), 8000),
    ),
  ])
}

export function browserPush(publicKey: string | undefined): AppPush {
  const key = publicKey?.trim() || null
  return {
    publicKey: key,
    supported: () =>
      key !== null &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window,
    needsInstall: () => apple() && !installed(),
    permission: () => ('Notification' in window ? Notification.permission : 'denied'),
    async subscribe() {
      if (!key) return null
      if ((await Notification.requestPermission()) !== 'granted') return null
      const reg = await registration()
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: b64urlToBytes(key),
        }))
      return device(sub)
    },
    async current() {
      if (!('serviceWorker' in navigator)) return null
      const reg = await navigator.serviceWorker.getRegistration()
      return device((await reg?.pushManager.getSubscription()) ?? null)
    },
    async unsubscribe() {
      if (!('serviceWorker' in navigator)) return null
      const reg = await navigator.serviceWorker.getRegistration()
      const sub = (await reg?.pushManager.getSubscription()) ?? null
      if (!sub) return null
      await sub.unsubscribe()
      return sub.endpoint
    },
  }
}
